import { SessionStorageKeys } from '@consts';
import { SessionStorageService } from '@hooks/useSessionStorage';

interface StoredDrafts {
  nodeEntityId: string;
  values: Record<string, string>;
}

const sessionStorageService = SessionStorageService.getInstance();

// Only text answers need a copy. An uploaded file already lives on the server and a game keeps
// its own state, so neither can be lost this way.
const pickTextDrafts = (source: Record<string, unknown>): Record<string, string> => {
  const values: Record<string, string> = {};
  Object.entries(source ?? {}).forEach(([blockEntityId, value]) => {
    if (typeof value === 'string' && value.trim()) values[blockEntityId] = value;
  });
  return values;
};

export function saveAssignmentTaskDrafts(
  nodeEntityId: string | null | undefined,
  taskOutput: Record<string, unknown>
): void {
  if (!nodeEntityId) return;

  const values = pickTextDrafts(taskOutput);
  if (!Object.keys(values).length) return;

  const payload: StoredDrafts = { nodeEntityId, values };
  sessionStorageService.writeJson(SessionStorageKeys.ASSIGNMENT_TASK_DRAFTS, payload);
}

export function readAssignmentTaskDrafts(
  nodeEntityId: string | null | undefined
): Record<string, string> | null {
  if (!nodeEntityId) return null;

  const parsed = sessionStorageService.readJson(SessionStorageKeys.ASSIGNMENT_TASK_DRAFTS) as {
    nodeEntityId?: unknown;
    values?: unknown;
  } | null;
  // A snapshot taken in another task would drop text into blocks it never belonged to.
  if (parsed?.nodeEntityId !== nodeEntityId) return null;
  // A string would be walked character by character, so anything else is treated as empty.
  if (typeof parsed?.values !== 'object' || parsed.values === null) return null;

  const values = pickTextDrafts(parsed.values as Record<string, unknown>);
  return Object.keys(values).length ? values : null;
}

export function clearAssignmentTaskDrafts(): void {
  sessionStorageService.remove(SessionStorageKeys.ASSIGNMENT_TASK_DRAFTS);
}
